import React, { useEffect, useState } from 'react';
import Icon from '@ant-design/icons';
import { Badge, Button, Popover, Tooltip } from 'antd';
import TuneOutlinedSVG from '@assets/TuneOutlined.svg?react';
import { INVALID_JSON_MESSAGE } from '@constants/common';
import { CONFIGURATIONS_TOOLTIP_TITLE, QUERY_PARAMETERS_TITLE } from '@constants/search';
import QueryParamsContent from '@components/QueryParamsContent/QueryParamsContent';
import styles from './queryPipelineParams.module.scss';

type Props = {
  params: Record<string, unknown>;
  onParamsChange: (params: Props['params']) => void;
  initialPrompts?: string[];
};

export const QueryPipelineParams: React.FC<Props> = ({
  onParamsChange,
  params,
  initialPrompts,
}) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(JSON.stringify(params, null, 4));
  const [jsonError, setJsonError] = useState<string | null>(null);

  useEffect(() => {
    try {
      const current = JSON.parse(value);
      const newChanges = [];

      for (const key of initialPrompts ?? []) {
        const entry = params?.[key];
        if (typeof entry === 'object' && entry !== null && !Array.isArray(entry)) {
          if ('template' in entry && entry?.template) {
            if (entry?.template != current[key]?.template) {
              current[key] = { ...current[key], ...entry };
              newChanges.push(key);
            }
          }
        }
      }

      if (newChanges.length) {
        setValue(JSON.stringify(current, null, 4));
      }
    } catch (error) {}
  }, [params]);

  useEffect(() => {
    try {
      const parsed = JSON.parse(value);
      setJsonError(null);
      for (const key of initialPrompts ?? []) {
        const entry = parsed?.[key];

        if (typeof entry === 'object' && entry !== null && !Array.isArray(entry)) {
          if (!('template' in entry)) {
            parsed[key] = { ...entry, template: '' };
          }
        } else {
          parsed[key] = { template: '' };
        }
      }

      onParamsChange(parsed);
    } catch (error) {
      if (!value.trim().length) {
        const defaults = Object.fromEntries(
          initialPrompts?.map((key) => [key, { template: '' }]) ?? [],
        );
        onParamsChange(defaults);
      }
      setJsonError(INVALID_JSON_MESSAGE);
    }
  }, [value]);

  const hasSetQueryPipelineParams = !!value && !jsonError;

  const handleOpenChange = (newOpenState: boolean) => {
    setOpen(newOpenState);
  };

  const onChange = (input: string) => {
    setValue(input);
  };

  return (
    <Popover
      content={<QueryParamsContent error={jsonError} value={value} onChange={onChange} />}
      title={QUERY_PARAMETERS_TITLE}
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
      placement="bottomRight"
      rootClassName={styles.paramsPopover}
    >
      <Tooltip title={CONFIGURATIONS_TOOLTIP_TITLE} placement="bottom">
        <Badge dot={hasSetQueryPipelineParams}>
          <Button type="text" icon={<Icon component={TuneOutlinedSVG} />} />
        </Badge>
      </Tooltip>
    </Popover>
  );
};
export default QueryPipelineParams;
